import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import PlaidConnect from '../components/PlaidConnect';
import MoovPayouts from '../components/MoovPayouts';
import { Building2, CreditCard, FileText, Zap } from 'lucide-react';
import { apiFetch } from '../lib/api';

export default function Integrations() {
  const [plaidConnected, setPlaidConnected] = useState(false);
  const [moovConnected, setMoovConnected] = useState(false);

  useEffect(() => {
    const checkConnections = async () => {
      try {
        const [plaidData, moovData] = await Promise.all([
          apiFetch<{ connected: boolean }>('/api/plaid/accounts'),
          apiFetch<{ connected: boolean }>('/api/moov/status'),
        ]);

        setPlaidConnected(plaidData.connected || false);
        setMoovConnected(moovData.connected || false);
      } catch (error) {
        console.error('Failed to check connections:', error);
      }
    };

    checkConnections();
  }, []);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Integrations</h1>
          <p className="text-slate-400">Connect your financial tools and services</p>
        </div>

        {/* Active Integrations */}
        <div className="grid lg:grid-cols-2 gap-6">
          <PlaidConnect />
          <MoovPayouts />
        </div>

        {/* Available Integrations */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Available Integrations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">QuickBooks</h3>
                  <p className="text-xs text-slate-500">Accounting</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Automatically import invoices and vendors from QuickBooks
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Xero</h3>
                  <p className="text-xs text-slate-500">Accounting</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Sync invoices and financial data with Xero accounting
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Stripe</h3>
                  <p className="text-xs text-slate-500">Payments</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Monitor subscription charges and detect anomalies
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Zapier</h3>
                  <p className="text-xs text-slate-500">Automation</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Connect Vigilant to thousands of apps via Zapier
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Bill.com</h3>
                  <p className="text-xs text-slate-500">AP Automation</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Import and validate bills automatically from Bill.com
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">NetSuite</h3>
                  <p className="text-xs text-slate-500">ERP</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-4">
                Enterprise-grade ERP integration for large organizations
              </p>
              <button className="w-full bg-slate-700/50 text-slate-300 py-2 rounded-lg font-medium hover:bg-slate-700 transition-all">
                Coming Soon
              </button>
            </div>
          </div>
        </div>

        {/* Integration Stats */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Integration Status</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-1">Active Connections</p>
              <p className="text-2xl font-bold text-white">
                {[plaidConnected, moovConnected].filter(Boolean).length}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Data Synced Today</p>
              <p className="text-2xl font-bold text-white">0</p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Last Sync</p>
              <p className="text-2xl font-bold text-white">-</p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
